/**
 * Toene (Masterprompt Abschnitte 5, 26: alles selbst erzeugt).
 *
 * Es gibt bewusst KEINE Tondateien. Jeder Ton wird zur Laufzeit aus
 * Grundschwingungen erzeugt - damit ist ausgeschlossen, dass fremdes Material
 * im Spiel landet, und es gibt nichts nachzuladen.
 *
 * Die Toene sind absichtlich karg: kurz, leise und klar unterscheidbar. Sie
 * sollen Rueckmeldung geben, nicht das Gefecht uebertoenen.
 *
 * Browser erlauben Ton erst nach einer Eingabe des Nutzers. Deshalb wird der
 * Tonapparat beim ersten Antippen gestartet und vorher stillschweigend
 * uebersprungen.
 */

export type SoundName =
  /** Der eigene Hero trifft. */
  | 'hit'
  /** Ein Skill wurde gewirkt. */
  | 'cast'
  /** Das eigene Portal hat Schaden bekommen. */
  | 'portal'
  /** Levelaufstieg. */
  | 'level'
  /** Kauf angenommen. */
  | 'buy'
  /** Etwas wurde abgelehnt. */
  | 'denied';

interface ToneSpec {
  /** Grundfrequenz in Hertz. */
  frequency: number;
  /** Zielfrequenz - erzeugt das Auf oder Ab im Ton. */
  toFrequency?: number;
  /** Dauer in Sekunden. */
  duration: number;
  /** Lautstaerke 0 bis 1, bevor die Gesamtlautstaerke greift. */
  gain: number;
  type: OscillatorType;
}

/**
 * Die Toene des Spiels.
 *
 * Tiefe Toene fuer Treffer und Gefahr, hoehere fuer Erfolge - das ist die
 * einzige "Melodie" hier, und sie soll ohne Hinsehen verstaendlich sein.
 */
const TONES: Readonly<Record<SoundName, ToneSpec>> = {
  hit: { frequency: 180, toFrequency: 120, duration: 0.07, gain: 0.25, type: 'square' },
  cast: { frequency: 420, toFrequency: 700, duration: 0.14, gain: 0.22, type: 'triangle' },
  portal: { frequency: 140, toFrequency: 70, duration: 0.45, gain: 0.5, type: 'sawtooth' },
  level: { frequency: 520, toFrequency: 990, duration: 0.3, gain: 0.3, type: 'sine' },
  buy: { frequency: 660, toFrequency: 880, duration: 0.1, gain: 0.2, type: 'sine' },
  denied: { frequency: 220, toFrequency: 150, duration: 0.16, gain: 0.25, type: 'square' },
};

/** Gesamtlautstaerke. Bewusst niedrig - das Spiel laeuft oft nebenbei. */
const MASTER_GAIN = 0.35;

/** Kuerzester Abstand zwischen zwei gleichen Toenen, in Millisekunden. */
const MIN_REPEAT_MS = 60;

export class Sound {
  private context: AudioContext | null = null;
  private master: GainNode | null = null;
  private zuletzt = new Map<SoundName, number>();
  private stumm = false;

  /**
   * Liegt das Fenster gerade im Hintergrund?
   *
   * Rueckmeldung vom 26.09.2026: Ein offener Tab mit laufendem Match toente
   * weiter, obwohl niemand hinsah - das Match laeuft auf dem Server ja weiter.
   * Ton gibt es deshalb nur, solange man das Spiel auch sieht.
   */
  private verdeckt = false;

  /** Meldet, ob das Fenster sichtbar ist. Stellt den Ton entsprechend ab. */
  setVisible(sichtbar: boolean): void {
    this.verdeckt = !sichtbar;
    this.applyGain();
  }

  /** Setzt die Lautstaerke aus Stummschaltung und Sichtbarkeit. */
  private applyGain(): void {
    if (!this.master) return;
    this.master.gain.value = this.stumm || this.verdeckt ? 0 : MASTER_GAIN;
  }

  /**
   * Startet den Tonapparat.
   *
   * Muss aus einer Eingabe heraus passieren - sonst lehnt der Browser ab.
   * Mehrfaches Aufrufen schadet nicht.
   */
  unlock(): void {
    if (this.context) {
      void this.context.resume();
      return;
    }

    const Ctor =
      window.AudioContext ??
      (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return;

    try {
      this.context = new Ctor();
      this.master = this.context.createGain();
      this.master.connect(this.context.destination);
      this.applyGain();
    } catch {
      // Kein Ton moeglich - das Spiel laeuft trotzdem weiter.
      this.context = null;
    }
  }

  /** Schaltet den Ton an oder aus. Gibt den neuen Zustand zurueck. */
  toggleMute(): boolean {
    this.stumm = !this.stumm;
    this.applyGain();
    return this.stumm;
  }

  isMuted(): boolean {
    return this.stumm;
  }

  /**
   * Spielt einen Ton.
   *
   * Gleiche Toene werden zusammengefasst: Wenn zwanzig Creeps im selben
   * Augenblick getroffen werden, soll es einmal klacken und nicht zwanzigmal.
   */
  play(name: SoundName): void {
    // Im Hintergrund bleibt es still - auch wenn das Match weiterlaeuft.
    if (this.stumm || this.verdeckt || !this.context || !this.master) return;

    const jetzt = performance.now();
    const letzter = this.zuletzt.get(name) ?? 0;
    if (jetzt - letzter < MIN_REPEAT_MS) return;
    this.zuletzt.set(name, jetzt);

    const spec = TONES[name];
    const context = this.context;
    const start = context.currentTime;

    const oszillator = context.createOscillator();
    oszillator.type = spec.type;
    oszillator.frequency.setValueAtTime(spec.frequency, start);
    if (spec.toFrequency) {
      oszillator.frequency.exponentialRampToValueAtTime(
        Math.max(1, spec.toFrequency),
        start + spec.duration,
      );
    }

    // Huellkurve: sofort da, dann weich aus. Ohne das knackt es beim Abbruch.
    const huelle = context.createGain();
    huelle.gain.setValueAtTime(0.0001, start);
    huelle.gain.exponentialRampToValueAtTime(spec.gain, start + 0.01);
    huelle.gain.exponentialRampToValueAtTime(0.0001, start + spec.duration);

    oszillator.connect(huelle);
    huelle.connect(this.master);
    oszillator.start(start);
    oszillator.stop(start + spec.duration + 0.02);
  }

  destroy(): void {
    void this.context?.close();
    this.context = null;
    this.master = null;
  }
}
